// प्रत्यय: the सामान्य (core) and विकल्प (variant) endings of every tense, person and number: what happens to the root
// first (अ → आ, अ → इ, अ → ए), the marker of the passive (-ईअ, -इज्ज) or of the causative (-आव, -आवे), and the ending.
//
// Three sources, all applied by the same rules:
// - book:    प्राकृत स्वयं-शिक्षक (the kit). Its सामान्य and विकल्प forms are exactly conjA / conjLong (verbs.ts);
//            runInvariants checks this for every kit root and tense.
// - notes:   the teacher's notes from प्राकृत-रचना-सौरभ (पाठ १–२०): every विकल्प of वर्तमान, विधि एवं आज्ञा, भूत and
//            the first two cells of भविष्यत्, for अकारान्त and आकारान्त आदि roots, and the subject pronouns हं, अम्मि …
// - pattern: the other भविष्यत् cells, which the notes do not cover yet, built by the notes' own formula
//            (भविष्यत् प्रत्यय हि / स्स / स्सि + the वर्तमान ending of that cell). Marked so a teacher can confirm them.
// withVikalp() adds the notes and pattern forms to the verb tables as 'alt', so the analyser, the sentence checker and
// the exercises accept them; the book's सामान्य form stays the one that is taught and asked first.

import type {
    Cell,
    FormStatus,
    Lang,
    Num,
    Person,
    PersonTable,
    RootClass,
    Tense,
    VerbForms
} from '@/prakrit/lib/grammar/types'

import { addMatra, nfc } from '@/prakrit/lib/grammar/devanagari'
import { showEnding } from '@/prakrit/lib/grammar/formation'
import { F } from '@/prakrit/lib/grammar/verbs'
import { NUMBERS, PERSONS, TENSES } from '@/prakrit/lib/grammar/types'

// ---------------------------------------------------------------- stems

/** How the root is prepared before the ending: पढ, पढा, पढे, पढि, पढी, पढीअ, पढिज्ज, पढाव, पढावे, पढावि, दे, ठ, ठाए. */
type StemOp = 'root' | 'aa' | 'e' | 'i' | 'ii' | 'ia' | 'ijj' | 'av' | 'ave' | 'avi' | 'de' | 'short' | 'ae'

const SHORT_OF: Record<string, string> = { 'ा': '', 'ी': 'ि', 'ू': 'ु' }

/** A long final vowel before a conjunct becomes short: ठा + न्ति → ठन्ति (हो stays: ओ counts as short). */
const shortenFinal = (s: string): string => {
    const last = s.slice(-1)

    return last in SHORT_OF ? s.slice(0, -1) + SHORT_OF[last] : s
}

const STEM: Record<StemOp, (root: string) => string> = {
    root: s => s,
    aa: s => addMatra(s, 'आ'),
    e: s => addMatra(s, 'ए'),
    i: s => addMatra(s, 'इ'),
    ii: s => addMatra(s, 'ई'),
    ia: s => addMatra(s, 'ई') + 'अ',
    ijj: s => addMatra(s, 'इ') + 'ज्ज',
    av: s => addMatra(s, 'आ') + 'व',
    ave: s => addMatra(s, 'आ') + 'वे',
    avi: s => addMatra(s, 'आ') + 'वि',
    de: () => 'दे',
    short: shortenFinal,
    ae: s => s + 'ए'
}

/** The passive or causative marker that sits between the root and the ending. */
const MARKER: Partial<Record<StemOp, string>> = {
    ia: 'ईअ',
    ijj: 'इज्ज',
    av: 'आव',
    ave: 'आवे',
    avi: 'आव'
}

export type StemChange = 'aa' | 'e' | 'i' | 'avi' | 'de' | 'short' | 'ae' | 'ai'

export const STEM_CHANGE: Record<StemChange, Lang> = {
    aa: { hi: 'अ → आ', en: 'अ becomes आ' },
    e: { hi: 'अ → ए', en: 'अ becomes ए' },
    i: { hi: 'अ → इ', en: 'अ becomes इ' },
    avi: { hi: 'आव का अ → इ', en: 'the अ of आव becomes इ' },
    de: { hi: 'दा → दे', en: 'दा becomes दे' },
    short: { hi: 'संयुक्त अक्षर से पहले दीर्घ स्वर ह्रस्व', en: 'a long vowel before a conjunct becomes short' },
    ae: { hi: 'अ विकरण जुड़कर अ → ए', en: 'the vikaraṇa अ is added and becomes ए' },
    ai: { hi: 'अ + इ = ए', en: 'अ + इ becomes ए' }
}

/** The vowel the root's अ turns into, shown in front of the ending (आमि, एमि, इमो). */
const VOWEL_OF: Partial<Record<StemChange, string>> = { aa: 'आ', e: 'ए', i: 'इ' }

// ---------------------------------------------------------------- specs

export type PratyayaSource = 'book' | 'notes' | 'pattern'

interface Spec {
    op: StemOp
    /** What is written after the stem (may start with a vowel sign: ित्था, ीअ, िरे). */
    add: string
    /** The ending as taught, as an independent string: मि, इत्था, ईअ ('' is the bare root). */
    ending: string
    change?: StemChange
    rule?: string
    source?: PratyayaSource
}

interface SpecOptions {
    ending?: string
    change?: StemChange
    rule?: string
    source?: PratyayaSource
}

const x = (op: StemOp, add: string, options: SpecOptions = {}): Spec => ({
    op,
    add,
    ending: options.ending ?? add,
    change: options.change,
    rule: options.rule,
    source: options.source
})

type CellKey = `${Person}.${Num}`
type CellSpecs = Partial<Record<CellKey, Spec[]>>

const CELL_KEYS: CellKey[] = PERSONS.flatMap(p => NUMBERS.map(n => `${p}.${n}` as CellKey))

/** The same endings in all six cells (भूत, क्रियातिपत्ति). */
const every = (specs: Spec[]): CellSpecs => Object.fromEntries(CELL_KEYS.map(k => [k, specs])) as CellSpecs

/** Add more endings to some cells. */
const merge = (...parts: CellSpecs[]): CellSpecs => {
    const out: CellSpecs = {}

    for (const part of parts) {
        for (const k of CELL_KEYS) {
            const list = part[k]

            if (list) out[k] = [...(out[k] ?? []), ...list]
        }
    }

    return out
}

/** One ending per cell, in the order मि मो · सि इत्था · इ न्ति (uttam.eka … anya.bahu). */
const six = (op: StemOp, adds: [string, string, string, string, string, string], options: Omit<SpecOptions, 'ending'> & { endings?: Partial<Record<number, string>> } = {}): CellSpecs => {
    const out: CellSpecs = {}

    CELL_KEYS.forEach((k, i) => {
        out[k] = [x(op, adds[i], { ending: options.endings?.[i], change: options.change, rule: options.rule, source: options.source })]
    })

    return out
}

type Vowel = [StemOp, StemChange | undefined]

const A: Vowel = ['root', undefined]
const AA: Vowel = ['aa', 'aa']
const E: Vowel = ['e', 'e']
const I: Vowel = ['i', 'i']

/** Every ending with every vowel change, ending by ending: हसमो, हसामो, हसेमो, हसिमो, हसमु … */
const each = (vowels: Vowel[], adds: string[], rule: string, source: PratyayaSource = 'notes'): Spec[] => (
    adds.flatMap(add => vowels.map(([op, change]) => x(op, add, { change, rule, source })))
)

/** The same endings for a root that does not change (आकारान्त आदि). */
const plain = (adds: string[], rule: string, source: PratyayaSource = 'notes'): Spec[] => adds.map(add => x('root', add, { rule, source }))

interface TenseSpecs {
    /** The book's सामान्य forms (conjA / conjLong core). */
    samanya: CellSpecs
    /** The book's विकल्प forms (conjA / conjLong alt). */
    vikalp: CellSpecs
    /** Every form the notes list for the cell, in the notes' order (the सामान्य form may be among them). */
    notes: CellSpecs
}

const RS = (n: string) => `रचना-सौरभ पाठ ${n}`
const PATTERN = 'रचना-सौरभ पाठ १९–२० के क्रम से (अनुमानित)'

/** हस + ज्ज / ज्जा in every cell of the present (पाठ ८): अ → ए first for अकारान्त roots. */
const JJ = (op: StemOp, rule: string, change?: StemChange): CellSpecs => every([
    x(op, 'ज्ज', { change, rule }),
    x(op, 'ज्जा', { change, rule })
])

const FUTURE_ENDINGS: [string, string, string, string, string, string] = ['हिमि', 'हामो', 'हिसि', 'हित्था', 'हिइ', 'हिंति']
const FUTURE_SS_ENDINGS: [string, string, string, string, string, string] = ['स्सामि', 'स्सामो', 'स्ससि', 'स्सह', 'स्सइ', 'स्संति']
const COMMAND_ENDINGS: [string, string, string, string, string, string] = ['मु', 'मो', 'हि', 'ह', 'उ', 'न्तु']

const NONE: TenseSpecs['notes'] = {}

// ---------------------------------------------------------------- अकारान्त धातु (पढ, हस, पास …)

const A_SPECS: Record<Tense, TenseSpecs> = {
    vartaman: {
        samanya: {
            'uttam.eka': [x('aa', 'मि', { change: 'aa', rule: 'नि.१०' })],
            'uttam.bahu': [x('aa', 'मो', { change: 'aa', rule: 'नि.१०' })],
            'madhyam.eka': [x('root', 'सि', { rule: 'नि.९' })],
            'madhyam.bahu': [x('i', 'त्था', { ending: 'इत्था', rule: 'नि.९' })],
            'anya.eka': [x('root', 'इ', { rule: 'नि.९' })],
            'anya.bahu': [x('root', 'न्ति', { rule: 'नि.९' })]
        },
        vikalp: six('e', ['मि', 'मो', 'सि', 'त्था', 'इ', 'ंति'], { change: 'e', rule: 'पाठ ८९' }),
        notes: merge(
            {
                'uttam.eka': each([A, AA, E], ['मि'], RS('१')),
                'uttam.bahu': each([A, AA, E, I], ['मो', 'मु', 'म'], RS('५')),
                'madhyam.eka': [...each([A, E], ['सि'], RS('२')), x('root', 'से', { rule: RS('२') })],
                'madhyam.bahu': [
                    ...each([A, E], ['ह', 'ध'], RS('६')),
                    x('i', 'त्था', { ending: 'इत्था', rule: RS('६') }),
                    x('e', 'इत्था', { change: 'e', rule: RS('६') })
                ],
                'anya.eka': [...each([A, E], ['इ', 'दि'], RS('३')), x('root', 'ए', { rule: RS('३') }), x('root', 'दे', { rule: RS('३') })],
                'anya.bahu': [
                    ...each([A, E], ['न्ति'], RS('७')),
                    x('root', 'न्ते', { rule: RS('७') }),
                    x('root', 'िरे', { ending: 'इरे', rule: RS('७') })
                ]
            },
            JJ('e', RS('८'), 'e')
        )
    },
    bhoot: {
        samanya: every([x('ii', 'अ', { ending: 'ईअ', rule: 'नि.११' })]),
        vikalp: {},
        notes: every([x('ii', 'अ', { ending: 'ईअ', rule: RS('१७') })])
    },
    bhavishya: {
        samanya: six('i', FUTURE_ENDINGS, { change: 'i', rule: 'नि.१३' }),
        vikalp: six('i', FUTURE_SS_ENDINGS, { change: 'i', rule: 'पाठ ८९' }),
        notes: {
            'uttam.eka': [
                ...each([I], ['हिमि', 'स्सामि', 'हामि', 'स्सिमि'], RS('१९')),
                ...each([E], ['हिमि', 'स्सामि', 'हामि'], RS('१९')),
                ...each([I, E], ['स्सं'], RS('१९'))
            ],
            'madhyam.eka': [
                ...each([I], ['हिसि', 'हिसे'], RS('२०')),
                ...each([E], ['हिसि', 'हिसे'], RS('२०')),
                ...each([I], ['स्ससि', 'स्ससे'], RS('२०')),
                ...each([E], ['स्ससि', 'स्ससे'], RS('२०')),
                ...each([I], ['स्सिसि', 'स्सिसे'], RS('२०'))
            ],
            'uttam.bahu': [
                ...each([I, E], ['हिमो', 'हिमु', 'हिम', 'स्सामो', 'स्सामु', 'स्साम', 'हामो', 'हामु', 'हाम'], PATTERN, 'pattern'),
                ...each([I], ['स्सिमो', 'स्सिमु', 'स्सिम'], PATTERN, 'pattern'),
                ...each([I, E], ['हिस्सा'], PATTERN, 'pattern')
            ],
            'madhyam.bahu': [
                ...each([I, E], ['हिह', 'हिध', 'हित्था', 'स्सह', 'स्सध', 'स्सित्था'], PATTERN, 'pattern'),
                ...each([I], ['स्सिह', 'स्सिध'], PATTERN, 'pattern')
            ],
            'anya.eka': [
                ...each([I, E], ['हिइ', 'हिए', 'हिदि', 'हिदे', 'स्सइ', 'स्सए', 'स्सदि', 'स्सदे'], PATTERN, 'pattern'),
                ...each([I], ['स्सिइ', 'स्सिए', 'स्सिदि', 'स्सिदे'], PATTERN, 'pattern')
            ],
            'anya.bahu': [
                ...each([I, E], ['हिन्ति', 'हिन्ते', 'स्सन्ति', 'स्सन्ते'], PATTERN, 'pattern'),
                ...each([I], ['स्सिन्ति', 'स्सिन्ते'], PATTERN, 'pattern')
            ]
        }
    },
    vidhi: {
        samanya: six('root', COMMAND_ENDINGS, { rule: 'नि.१४' }),
        vikalp: {
            'madhyam.eka': [x('root', '', { rule: 'पाठ ८९' }), x('root', 'सु', { rule: 'पाठ ८९' })]
        },
        notes: {
            'uttam.eka': each([A, AA, I, E], ['मु'], RS('९')),
            'uttam.bahu': each([A, AA, E], ['मो'], RS('१३')),
            'madhyam.eka': [
                ...each([A], ['हि', 'सु', 'धि'], RS('१०')),
                x('root', '', { rule: RS('१०') }),
                ...each([E], ['हि', 'सु', 'धि'], RS('१०')),
                x('e', 'ज्जसु', { ending: 'इज्जसु', change: 'ai', rule: RS('१०') }),
                x('e', 'ज्जहि', { ending: 'इज्जहि', change: 'ai', rule: RS('१०') }),
                x('e', 'ज्जे', { ending: 'इज्जे', change: 'ai', rule: RS('१०') })
            ],
            'madhyam.bahu': each([A, E], ['ह', 'ध'], RS('१४')),
            'anya.eka': each([A, E], ['उ', 'दु'], RS('११')),
            'anya.bahu': each([A, E], ['न्तु'], RS('१५'))
        }
    },
    karmani_vartaman: {
        samanya: merge(
            six('ia', ['मि', 'मो', 'सि', 'इत्था', 'इ', 'ंति'], { rule: 'नि.८३' }),
            six('ijj', ['मि', 'मो', 'सि', 'ित्था', 'इ', 'ंति'], { rule: 'नि.८३', endings: { 3: 'इत्था' } })
        ),
        vikalp: {},
        notes: NONE
    },
    karmani_bhoot: {
        samanya: every([x('ia', 'ईअ', { rule: 'नि.८३' }), x('ijj', 'ीअ', { ending: 'ईअ', rule: 'नि.८३' })]),
        vikalp: {},
        notes: NONE
    },
    karmani_vidhi: {
        samanya: merge(
            six('ia', ['मु', 'मो', 'हि', 'ह', 'उ', 'ंतु'], { rule: 'नि.८३' }),
            six('ijj', ['मु', 'मो', 'हि', 'ह', 'उ', 'ंतु'], { rule: 'नि.८३' })
        ),
        vikalp: {},
        notes: NONE
    },
    prerak_vartaman: {
        samanya: six('av', ['मि', 'मो', 'सि', 'ित्था', 'इ', 'न्ति'], { rule: 'नि.९५', endings: { 3: 'इत्था' } }),
        vikalp: six('ave', ['मि', 'मो', 'सि', 'इत्था', 'इ', 'ंति'], { rule: 'पाठ ८१' }),
        notes: NONE
    },
    prerak_bhoot: {
        samanya: every([x('av', 'ीअ', { ending: 'ईअ', rule: 'नि.९५' })]),
        vikalp: {},
        notes: NONE
    },
    prerak_bhavishya: {
        samanya: six('avi', FUTURE_ENDINGS, { change: 'avi', rule: 'नि.९५' }),
        vikalp: {},
        notes: NONE
    },
    prerak_vidhi: {
        samanya: six('av', COMMAND_ENDINGS, { rule: 'नि.९५' }),
        vikalp: {},
        notes: NONE
    },
    kriyatipatti: {
        samanya: every([
            x('e', 'ज्ज', { change: 'e', rule: 'नि.१०२' }),
            x('e', 'ज्जा', { change: 'e', rule: 'नि.१०२' }),
            x('root', 'न्तो', { rule: 'नि.१०२' }),
            x('root', 'माणो', { rule: 'नि.१०२' })
        ]),
        vikalp: {},
        notes: NONE
    }
}

// ---------------------------------------------------------------- आ / ए / ओ-ending roots (ठा, ण्हा, हो, दा …)

const LONG_SPECS: Partial<Record<Tense, TenseSpecs>> = {
    vartaman: {
        samanya: {
            'uttam.eka': [x('root', 'मि', { rule: 'नि.१०' })],
            'uttam.bahu': [x('root', 'मो', { rule: 'नि.१०' })],
            'madhyam.eka': [x('root', 'सि', { rule: 'नि.९' })],
            'madhyam.bahu': [x('root', 'इत्था', { rule: 'नि.९' })],
            'anya.eka': [x('root', 'इ', { rule: 'नि.९' })],
            'anya.bahu': [x('root', 'न्ति', { rule: 'नि.९' })]
        },
        // Only दा has book विकल्प forms here (दे-): see DE_ALT below.
        vikalp: {},
        notes: merge(
            {
                'uttam.eka': plain(['मि'], RS('४')),
                'uttam.bahu': plain(['मो', 'मु', 'म'], RS('८')),
                'madhyam.eka': plain(['सि'], RS('४')),
                'madhyam.bahu': plain(['ह', 'इत्था', 'ध'], RS('८')),
                'anya.eka': plain(['इ', 'दि'], RS('४')),
                'anya.bahu': [
                    x('short', 'न्ति', { change: 'short', rule: RS('८') }),
                    x('short', 'न्ते', { change: 'short', rule: RS('८') }),
                    x('root', 'इरे', { rule: RS('८') })
                ]
            },
            every([
                x('root', 'ज्ज', { rule: RS('८') }),
                x('root', 'ज्जा', { rule: RS('८') }),
                x('ae', 'ज्ज', { change: 'ae', rule: RS('८') }),
                x('ae', 'ज्जा', { change: 'ae', rule: RS('८') })
            ])
        )
    },
    bhoot: {
        samanya: every([x('root', 'ही', { rule: 'नि.१२' })]),
        vikalp: every([x('root', 'सी', { rule: 'पाठ ८९' }), x('root', 'हीअ', { rule: 'पाठ ८९' })]),
        notes: every(plain(['सी', 'ही', 'हीअ'], RS('१८')))
    },
    bhavishya: {
        samanya: six('root', FUTURE_ENDINGS, { rule: 'नि.१३' }),
        vikalp: six('root', FUTURE_SS_ENDINGS, { rule: 'पाठ ८९' }),
        notes: {
            'uttam.eka': plain(['हिमि', 'स्सामि', 'हामि', 'स्सं'], PATTERN, 'pattern'),
            'uttam.bahu': plain(['हिमो', 'हिमु', 'हिम', 'स्सामो', 'स्सामु', 'स्साम', 'हामो', 'हामु', 'हाम', 'हिस्सा'], PATTERN, 'pattern'),
            'madhyam.eka': plain(['हिसि', 'स्ससि'], PATTERN, 'pattern'),
            'madhyam.bahu': plain(['हिह', 'हिध', 'हित्था', 'स्सह', 'स्सध', 'स्सित्था'], PATTERN, 'pattern'),
            'anya.eka': plain(['हिइ', 'हिदि', 'स्सइ', 'स्सदि'], PATTERN, 'pattern'),
            'anya.bahu': plain(['हिन्ति', 'हिन्ते', 'स्सन्ति', 'स्सन्ते'], PATTERN, 'pattern')
        }
    },
    vidhi: {
        samanya: six('root', COMMAND_ENDINGS, { rule: 'नि.१४' }),
        vikalp: {},
        notes: {
            'uttam.eka': plain(['मु'], RS('१२')),
            'uttam.bahu': plain(['मो'], RS('१६')),
            'madhyam.eka': plain(['हि', 'सु', 'धि'], RS('१२')),
            'madhyam.bahu': plain(['ह', 'ध'], RS('१६')),
            'anya.eka': plain(['उ', 'दु'], RS('१२')),
            'anya.bahu': [x('short', 'न्तु', { change: 'short', rule: RS('१६') })]
        }
    },
    kriyatipatti: {
        samanya: every([
            x('root', 'ज्ज', { rule: 'नि.१०२' }),
            x('root', 'ज्जा', { rule: 'नि.१०२' }),
            x('root', 'न्तो', { rule: 'नि.१०२' }),
            x('root', 'माणो', { rule: 'नि.१०२' })
        ]),
        vikalp: {},
        notes: NONE
    }
}

/** दा alone has the दे- variants (पाठ ८९): देमि … देंति, देहि. */
const DE_ALT: Partial<Record<Tense, CellSpecs>> = {
    vartaman: six('de', ['मि', 'मो', 'सि', 'इत्था', 'इ', 'ंति'], { change: 'de', rule: 'पाठ ८९' }),
    vidhi: { 'madhyam.eka': [x('de', 'हि', { change: 'de', rule: 'पाठ ८९' })] }
}

// ---------------------------------------------------------------- applied to a root

export interface PratyayaForm {
    /** The finished form for this root. */
    form: string
    /** The ending as taught (मि, इत्था, ईअ …); '' is the bare root (शून्य प्रत्यय). */
    ending: string
    /** -ईअ / -इज्ज (passive) or -आव / -आवे (causative) between the root and the ending. */
    marker?: string
    /** The stem the ending is added to, after any change (पढा, पढि, पढीअ, पढाव, ठ …). */
    stem: string
    change?: StemChange
    status: Extract<FormStatus, 'core' | 'alt'>
    source: PratyayaSource
    rule?: string
}

export interface PratyayaCell {
    /** सामान्य: the forms the book teaches first. */
    samanya: PratyayaForm[]
    /** विकल्प: every other accepted form (the book's विकल्प, the notes' विकल्प and the pattern forms). */
    vikalp: PratyayaForm[]
    /** Every form of the cell in the notes' order (सामान्य included), as the विकल्प view lists them. */
    all: PratyayaForm[]
}

export type PratyayaTable = Record<Person, Record<Num, PratyayaCell>>

const isLongRoot = (root: string): boolean => /[ाेो]$|[आएओ]$/.test(root)

const specsFor = (tense: Tense, rootClass: RootClass): TenseSpecs | undefined => (
    rootClass === 'long' ? LONG_SPECS[tense] : A_SPECS[tense]
)

/** Does this root class have endings (and a verb table) for this tense? */
export const hasPratyaya = (tense: Tense, rootClass: RootClass): boolean => Boolean(specsFor(tense, rootClass))

/** The tenses a root class conjugates in, in TENSES order. */
export const pratyayaTenses = (rootClass: RootClass): Tense[] => TENSES.filter(t => hasPratyaya(t, rootClass))

/**
 * Two spellings of the same form count as one: the anusvāra before a dental is the dental nasal (पढेंति = पढेन्ति,
 * पढिहिंति = पढिहिन्ति). The book's spelling is the one kept.
 */
export const formKey = (form: string): string => nfc(form).replace(/ं(?=[तथदध])/g, 'न्')

const apply = (root: string, spec: Spec, status: 'core' | 'alt', fallback: PratyayaSource): PratyayaForm => {
    const stem = STEM[spec.op](root)
    const out: PratyayaForm = { form: nfc(stem + spec.add), ending: nfc(spec.ending), stem: nfc(stem), status, source: spec.source ?? fallback }
    const marker = MARKER[spec.op]

    if (marker) out.marker = marker
    if (spec.change && !(spec.change === 'short' && stem === root)) out.change = spec.change
    if (spec.rule) out.rule = spec.rule

    return out
}

/** The book's own forms of one cell, in the book's order: its सामान्य forms, then its विकल्प forms. */
const bookCell = (tense: Tense, root: string, rootClass: RootClass, k: CellKey): PratyayaForm[] => {
    const r = nfc(root)
    const specs = specsFor(tense, rootClass)

    if (!specs || r === 'अस') return []

    const extraAlt = rootClass === 'long' && r === 'दा' ? DE_ALT[tense] ?? {} : {}

    return [
        ...(specs.samanya[k] ?? []).map(s => apply(r, s, 'core', 'book')),
        ...[...(specs.vikalp[k] ?? []), ...(extraAlt[k] ?? [])].map(s => apply(r, s, 'alt', 'book'))
    ]
}

/**
 * Every ending of one tense for one root, per person and number: the book's सामान्य forms, every विकल्प (the book's,
 * the notes', the pattern's) and the whole list in the notes' order. Null when the root class has no such tense
 * (आ / ए / ओ roots have no कर्मवाच्य or प्रेरणार्थक tables) or the root is अस (irregular).
 */
export const pratyayaTable = (tense: Tense, root: string, rootClass: RootClass = isLongRoot(root) ? 'long' : 'a'): PratyayaTable | null => {
    const r = nfc(root)

    if (r === 'अस') return null

    const specs = specsFor(tense, rootClass)

    if (!specs) return null

    const out = {} as PratyayaTable

    for (const p of PERSONS) {
        out[p] = {} as Record<Num, PratyayaCell>

        for (const n of NUMBERS) {
            const k: CellKey = `${p}.${n}`
            const book = bookCell(tense, r, rootClass, k)
            const samanya = book.filter(f => f.status === 'core')
            const bookAlt = book.filter(f => f.status === 'alt')
            const known = new Map([...samanya, ...bookAlt].map(f => [formKey(f.form), f]))
            const seen = new Set<string>()
            const ordered: PratyayaForm[] = []

            for (const s of specs.notes[k] ?? []) {
                const f = apply(r, s, 'alt', 'notes')
                const key = formKey(f.form)

                if (seen.has(key)) continue

                seen.add(key)
                ordered.push(known.get(key) ?? f)
            }

            const missingCore = samanya.filter(f => !seen.has(formKey(f.form)))

            for (const f of missingCore) seen.add(formKey(f.form))

            const missingAlt = bookAlt.filter(f => !seen.has(formKey(f.form)))
            const all = [...missingCore, ...ordered, ...missingAlt]

            out[p][n] = { samanya, vikalp: all.filter(f => f.status === 'alt'), all }
        }
    }

    return out
}

/** Are all six cells the same (भूत, क्रियातिपत्ति)? */
export const sameInEveryCell = (table: PratyayaTable): boolean => {
    const key = (c: PratyayaCell) => c.all.map(f => f.form).join('|')
    const first = key(table.uttam.eka)

    return PERSONS.every(p => NUMBERS.every(n => key(table[p][n]) === first))
}

/** The ending as shown on a chip: "मि", "◌ंति", "ईअ + मि", "०" for the bare root (शून्य प्रत्यय). */
export const endingLabel = (f: Pick<PratyayaForm, 'ending' | 'marker'>): string => {
    const ending = f.ending ? showEnding(f.ending) : '०'

    return f.marker ? `${f.marker} + ${ending}` : ending
}

/** The ending together with the root's changed vowel, as the notes write it: मि / आमि / एमि, इत्था / एइत्था. */
export const fullEnding = (f: Pick<PratyayaForm, 'ending' | 'marker' | 'change'>): string => {
    const vowel = f.change === 'ae' ? 'ए' : f.change ? VOWEL_OF[f.change] ?? '' : ''
    const ending = vowel ? vowel + f.ending : f.ending ? showEnding(f.ending) : '०'

    return f.marker ? `${f.marker} + ${ending}` : ending
}

/** The distinct full endings of a list, in order. */
export const fullEndings = (forms: PratyayaForm[]): string[] => Array.from(new Set(forms.map(fullEnding)))

/**
 * The notes' formula for a cell, one line per ending: "हस + (अ/आ/ए) + मि", "हस + से", "हस + (इ/ए) + हिमि".
 * Vowels in brackets are what the root's last अ may become before that ending.
 */
export const formulaOf = (root: string, forms: PratyayaForm[]): string[] => {
    const groups = new Map<string, Set<string>>()

    for (const f of forms) {
        const key = f.change === 'ae' ? fullEnding(f) : endingLabel(f)
        const vowel = f.change && VOWEL_OF[f.change] ? VOWEL_OF[f.change] as string : 'अ'
        const set = groups.get(key) ?? new Set<string>()

        set.add(vowel)
        groups.set(key, set)
    }

    const order = ['अ', 'आ', 'ए', 'इ']

    return Array.from(groups, ([ending, vowels]) => {
        const list = order.filter(v => vowels.has(v))

        return list.length === 1 && list[0] === 'अ' ? `${root} + ${ending}` : `${root} + (${list.join('/')}) + ${ending}`
    })
}

// ---------------------------------------------------------------- subjects (प्रथमा pronouns)

/**
 * The subject pronouns of each cell as the notes give them (रचना-सौरभ पाठ १–८): the विकल्प view pairs every one of
 * them with every form. हं, अम्मि, वयं, तुं, तुह, तुब्भे, तुज्झे and ताउ are also विकल्प rows of the pronoun tables
 * (pronouns.ts); ता is shown here only, because ता is also the अव्यय "तो" of जइ … ता.
 */
export const VIKALP_SUBJECTS: Record<Person, Record<Num, string[]>> = {
    uttam: { eka: ['अहं', 'हं', 'अम्मि'], bahu: ['अम्हे', 'वयं'] },
    madhyam: { eka: ['तुमं', 'तुं', 'तुह'], bahu: ['तुब्भे', 'तुम्हे', 'तुज्झे'] },
    anya: { eka: ['सो', 'सा'], bahu: ['ते', 'ता', 'ताओ', 'ताउ'] }
}

// ---------------------------------------------------------------- the verb tables

/**
 * The verb tables with every notes and pattern विकल्प added as 'alt' (after the book's own forms, never replacing them).
 * The book tables themselves are untouched, so data/verbs.json still matches conjA / conjLong.
 */
export const withVikalp = (root: string, rootClass: RootClass, forms: VerbForms): VerbForms => {
    const out = { ...forms } as VerbForms

    for (const tense of TENSES) {
        const grid = forms[tense]

        if (!grid) continue

        const table = pratyayaTable(tense, root, rootClass)

        if (!table) continue

        const next = {} as PersonTable

        for (const p of PERSONS) {
            next[p] = {} as Record<Num, Cell>

            for (const n of NUMBERS) {
                const cell = grid[p][n].map(f => ({ ...f }))
                const have = new Set(cell.map(f => formKey(f.form)))

                for (const f of table[p][n].all) {
                    const key = formKey(f.form)

                    if (have.has(key)) continue

                    have.add(key)
                    cell.push(F(f.form, 'alt', f.rule, f.source === 'pattern' ? 'अनुमानित: नोट्स में यह खाना अभी नहीं है' : undefined))
                }

                next[p][n] = cell
            }
        }

        out[tense] = next
    }

    return out
}

// ---------------------------------------------------------------- the check against the book's verb tables

const cellList = (cell: Cell | undefined): string[] => (cell ?? []).map(f => `${f.form}:${f.status}`)

/**
 * Compare the book endings with a root's book verb tables (conjA / conjLong, before withVikalp), tense by tense and
 * cell by cell (forms, statuses and order). Returns one line per difference; an empty list means they agree.
 */
export const pratyayaMismatches = (root: string, rootClass: RootClass, tables: Partial<Record<Tense, PersonTable>>): string[] => {
    const out: string[] = []

    for (const tense of TENSES) {
        const grid = tables[tense]
        const table = pratyayaTable(tense, root, rootClass)

        if (!grid && !table) continue

        if (!grid || !table) {
            out.push(`${root} · ${tense}: ${grid ? 'no endings' : 'no verb table'}`)
            continue
        }

        for (const p of PERSONS) {
            for (const n of NUMBERS) {
                const want = cellList(grid[p][n])
                const got = bookCell(tense, root, rootClass, `${p}.${n}`).map(f => `${f.form}:${f.status}`)

                if (want.join('|') !== got.join('|')) out.push(`${root} · ${tense} · ${p}.${n}: ${got.join(' ')} ≠ ${want.join(' ')}`)
            }
        }
    }

    return out
}
